/**
 * client/src/services/TrackRepository.ts
 *
 * FASE REFACTOR (Agosto 2026) — Estratto da gpsTrackPersistence.ts (God Object → SRP).
 *
 * RESPONSABILITÀ UNICA: IndexedDB CRUD per sessioni di tracking GPS.
 * - Schema DB e lifecycle (open, corruption recovery)
 * - Quota management
 * - Session CRUD (create, flush, update, resume, delete)
 *
 * NON GESTISCE:
 * - Offline queue → TrackSyncQueue.ts
 * - Process death protection → TrackSyncQueue.ts
 * - Serializzazione/compressione → @/utils/geo
 */

import Dexie, { type Table } from 'dexie';
import { type GpsWaypoint } from '@/utils/geo';

// Re-export per retrocompatibilità
export type { GpsWaypoint };

// ─── Tipi ───────────────────────────────────────────────────────────────────

export interface TrackSession {
  id?: number;
  sessionId: string;
  startedAt: number;
  name: string;
  description: string;
  difficulty: 'easy' | 'medium' | 'hard';
  isPublic: boolean;
  waypoints: GpsWaypoint[];
  distanceKm: number;
  lastFlushedAt: number;
  rideguardToken: string | null;
}

export interface IndexedDbQuotaInfo {
  quota: number;
  usage: number;
  usagePercent: number;
  free: number;
  isCritical: boolean;
  isWarning: boolean;
  message: string;
}

// ─── IndexedDB Database ────────────────────────────────────────────────────

class GpsTrackDb extends Dexie {
  sessions!: Table<TrackSession, number>;

  constructor() {
    super('RideConnectGpsTracks');
    this.version(3).stores({
      sessions: '++id, sessionId, startedAt',
    });
  }
}

let _db: GpsTrackDb | null = null;
let _dbOpenPromise: Promise<void> | null = null;
let _dbCorrupted = false;

function getDb(): GpsTrackDb {
  if (!_db) _db = new GpsTrackDb();
  return _db;
}

export function wasDbCorrupted(): boolean {
  return _dbCorrupted;
}

// ─── DB Lifecycle ───────────────────────────────────────────────────────────

async function safeDbOpen(): Promise<void> {
  if (_dbOpenPromise) return _dbOpenPromise;

  _dbOpenPromise = (async () => {
    try {
      await getDb().open();
    } catch (err: any) {
      const isCorruption =
        err?.name === 'AbortError' ||
        err?.name === 'UnknownError' ||
        err?.name === 'VersionError' ||
        err?.message?.includes('corrupt') ||
        err?.message?.includes('database') ||
        err?.inner?.name === 'AbortError';

      if (isCorruption) {
        console.error('[TrackRepository] ⚠️ IndexedDB CORRUPTED — resetting');
        _dbCorrupted = true;

        if (typeof window !== 'undefined') {
          window.dispatchEvent(new CustomEvent('rideconnect:db-corrupted', {
            detail: {
              message: 'Il database locale era corrotto ed è stato ripristinato.',
              error: err?.message || 'Unknown corruption',
            },
          }));
        }

        try {
          await Dexie.delete('RideConnectGpsTracks');
          _db = new GpsTrackDb();
          await _db.open();
        } catch (resetErr) {
          console.error('[TrackRepository] CRITICAL: Failed to reset corrupted DB:', resetErr);
          throw new Error('Impossibile accedere al database locale. Riavvia l\'app.');
        }
      } else {
        console.error('[TrackRepository] DB open failed:', err);
        throw err;
      }
    }
  })();

  return _dbOpenPromise;
}

async function ensureDbReady(): Promise<void> {
  await safeDbOpen();
}

// ─── Safe Write con Quota Recovery ─────────────────────────────────────────

async function safeDbWrite<T>(
  operation: () => Promise<T>,
  context: string,
): Promise<{ success: boolean; result?: T }> {
  try {
    const result = await operation();
    return { success: true, result };
  } catch (err: any) {
    const isQuotaError =
      err?.name === 'QuotaExceededError' ||
      err?.message?.includes('quota') ||
      err?.message?.includes('space') ||
      err?.inner?.name === 'QuotaExceededError';

    if (!isQuotaError) {
      console.error(`[TrackRepository] ${context} failed:`, err);
      return { success: false };
    }

    console.warn(`[TrackRepository] ⚠️ Quota exceeded during ${context} — pruning`);
    try {
      const db = getDb();
      const oldSessions = await db.sessions.orderBy('startedAt').limit(5).toArray();
      const idsToDelete = oldSessions
        .filter(s => !context.includes(s.sessionId))
        .map(s => s.id!)
        .slice(0, 3);
      if (idsToDelete.length > 0) await db.sessions.bulkDelete(idsToDelete);
    } catch {}

    try {
      const result = await operation();
      return { success: true, result };
    } catch (retryErr) {
      console.error(`[TrackRepository] ${context} failed after pruning:`, retryErr);
      return { success: false };
    }
  }
}

// ─── Quota Management ──────────────────────────────────────────────────────

export async function checkIndexedDbQuota(): Promise<IndexedDbQuotaInfo> {
  try {
    if ('storage' in navigator && 'estimate' in navigator.storage) {
      const estimate = await navigator.storage.estimate();
      const quota = estimate.quota ?? 0;
      const usage = estimate.usage ?? 0;
      const free = quota - usage;
      const usagePercent = quota > 0 ? (usage / quota) * 100 : 0;
      const isCritical = free < 10 * 1024 * 1024;
      const isWarning = !isCritical && free < 25 * 1024 * 1024;

      let message: string;
      if (isCritical) {
        message = `⚠️ Spazio quasi esaurito (${Math.round(free / 1024 / 1024)}MB liberi). Libera spazio.`;
      } else if (isWarning) {
        message = `Spazio ridotto (${Math.round(free / 1024 / 1024)}MB liberi).`;
      } else {
        message = `Spazio sufficiente (${Math.round(free / 1024 / 1024)}MB liberi).`;
      }
      return { quota, usage, usagePercent, free, isCritical, isWarning, message };
    }
  } catch {}

  return { quota: 0, usage: 0, usagePercent: 0, free: 0, isCritical: false, isWarning: false, message: 'Impossibile verificare lo spazio.' };
}

export async function hasEnoughStorage(): Promise<boolean> {
  const info = await checkIndexedDbQuota();
  if (info.quota === 0) return true;
  return !info.isCritical;
}

// ─── Session CRUD ──────────────────────────────────────────────────────────

export async function createSession(params: {
  name: string;
  description: string;
  difficulty: 'easy' | 'medium' | 'hard';
  isPublic: boolean;
  rideguardToken: string | null;
}): Promise<string> {
  await ensureDbReady();
  const sessionId = crypto.randomUUID();
  const now = Date.now();

  await safeDbWrite(async () => {
    await getDb().sessions.add({
      sessionId,
      startedAt: now,
      name: params.name,
      description: params.description,
      difficulty: params.difficulty,
      isPublic: params.isPublic,
      waypoints: [],
      distanceKm: 0,
      lastFlushedAt: now,
      rideguardToken: params.rideguardToken,
    });
  }, `createSession(${sessionId})`);

  return sessionId;
}

export async function flushBuffer(
  sessionId: string,
  waypoints: GpsWaypoint[],
  distanceKm: number,
): Promise<void> {
  await ensureDbReady();
  const db = getDb();

  await safeDbWrite(async () => {
    let session = await db.sessions.where('sessionId').equals(sessionId).first();
    if (!session) {
      await db.sessions.add({
        sessionId,
        startedAt: waypoints[0]?.ts ?? Date.now(),
        name: '', description: '', difficulty: 'easy', isPublic: false,
        waypoints, distanceKm, lastFlushedAt: Date.now(), rideguardToken: null,
      });
      return;
    }
    await db.sessions.update(session.id!, { waypoints, distanceKm, lastFlushedAt: Date.now() });
  }, `flushBuffer(${sessionId})`);
}

export async function updateSessionMeta(
  sessionId: string,
  meta: {
    name?: string; description?: string;
    difficulty?: 'easy' | 'medium' | 'hard';
    isPublic?: boolean; rideguardToken?: string | null;
  },
): Promise<void> {
  try {
    await ensureDbReady();
    const session = await getDb().sessions.where('sessionId').equals(sessionId).first();
    if (!session) return;

    const updates: Partial<TrackSession> = {};
    if (meta.name !== undefined) updates.name = meta.name;
    if (meta.description !== undefined) updates.description = meta.description;
    if (meta.difficulty !== undefined) updates.difficulty = meta.difficulty;
    if (meta.isPublic !== undefined) updates.isPublic = meta.isPublic;
    if (meta.rideguardToken !== undefined) updates.rideguardToken = meta.rideguardToken;

    await getDb().sessions.update(session.id!, updates);
  } catch (err) {
    console.error('[TrackRepository] updateSessionMeta failed:', err);
  }
}

export async function resumeSession(): Promise<TrackSession | null> {
  try {
    await ensureDbReady();
    const sessions = await getDb().sessions.orderBy('startedAt').reverse().limit(1).toArray();
    if (sessions.length === 0) return null;

    const session = sessions[0];
    const maxAge = 12 * 60 * 60 * 1000;
    if (Date.now() - session.startedAt > maxAge) {
      await deleteSession(session.sessionId);
      return null;
    }
    return session;
  } catch (err) {
    console.error('[TrackRepository] resumeSession failed:', err);
    return null;
  }
}

export async function deleteSession(sessionId: string): Promise<void> {
  try {
    await ensureDbReady();
    await getDb().sessions.where('sessionId').equals(sessionId).delete();
  } catch (err) {
    console.error('[TrackRepository] deleteSession failed:', err);
  }
}

/**
 * FEAT NO-LOGOUT-DURING-RECORDING (18 Set 2026): true se esiste una
 * registrazione attiva/interrotta con waypoint (età < 12h).
 * Read-only — usata dal guard auth per NON buttare fuori l'utente
 * mentre registra un percorso.
 */
export async function hasActiveRecordingSession(): Promise<boolean> {
  try {
    await ensureDbReady();
    const sessions = await getDb().sessions.toArray();
    const now = Date.now();
    const maxAge = 12 * 60 * 60 * 1000;
    return sessions.some(
      (s) => now - s.startedAt <= maxAge && (s.waypoints?.length ?? 0) > 0,
    );
  } catch {
    return false;
  }
}

export async function deleteAllSessions(): Promise<void> {
  try {
    await ensureDbReady();
    await getDb().sessions.clear();
  } catch (err) {
    console.error('[TrackRepository] deleteAllSessions failed:', err);
  }
}
